/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Divider } from '@mui/material'
import Tile from '@/components/Tile/Tile'
import Grid from '@/config/Grid/Grid'
import Button from '@/components/Button/Button'
import OrderTile from '@/components/OrderTile/OrderTile'
import SummaryItemComponent from '@/components/SummaryItemComponent/SummaryItemComponent'
import GuestsList from '@/layouts/OrderPage/components/GuestsList/GuestsList'
import { useOrderPageQuery } from '@/layouts/OrderPage/hooks/useOrderPageQuery'
import OrderPageAPI from '@/layouts/OrderPage/api/OrderPageAPI'
import TablesAPI from '@/layouts/TablesList/api/TablesAPI/TablesAPI'
import { socketService } from '@/services/socket.service'
import { WebSocketEvent } from '@/services/webSocketEvent'
import {
  primaryColors,
  whiteShades
} from '@/assets/styles/helpers/colors.styles'
import styles from './ServeOrder.module.scss'

const CATEGORY_PASTEL_COLORS = [
  '#E57373', // soft red (All categories)
  '#81C784', // soft green
  '#64B5F6', // soft blue
  '#FFB74D', // soft orange
  '#BA68C8', // soft purple
  '#4DB6AC', // soft teal
  '#F06292', // soft pink
  '#A1887F', // soft brown
  '#7986CB', // soft indigo
  '#FFD54F', // soft amber
  '#AED581', // soft lime
  '#4DD0E1' // soft cyan
]

type TranslationModel = { languageCode: string; name: string }

type ApiTopping = {
  id: string
  quantity: number
  price?: number
  translations?: TranslationModel[]
}

type ApiSize = {
  id: string
  price: number
  translations: TranslationModel[]
}

type ApiDish = {
  id: string
  dishId: string
  price: number
  extraNote: string
  toppings: ApiTopping[]
  translations: TranslationModel[]
  size?: ApiSize
}

type ApiGuest = {
  guestNumber: number
  dishes: ApiDish[]
}

type ApiOrderResponse = {
  tableIds: string[]
  guests: ApiGuest[]
}

function ServeOrder() {
  const { tableId } = useParams<{ tableId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const tableStatus = (location.state as { tableStatus?: string })?.tableStatus
  const isPaid = tableStatus === 'paid'
  const defaultLanguage = localStorage.getItem('i18nextLng') || 'en'

  const [activeGuestNumber, setActiveGuestNumber] = useState(1)
  const [selectedCategoryTile, setSelectedCategoryTile] = useState(0)

  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['orders', tableId],
    queryFn: () =>
      OrderPageAPI.getOrders(tableId!) as unknown as Promise<ApiOrderResponse>,
    enabled: !!tableId
  })

  const { dishCategoriesList, dishesList } = useOrderPageQuery()

  const mappedDishCategoriesList = dishCategoriesList?.map(category => ({
    id: category.id,
    title: category.translations.find(t => t.languageCode === defaultLanguage)
      ?.name
  }))

  const mappedDishesList = dishesList?.map(dish => ({
    id: dish.id,
    title: dish.translations.find(t => t.languageCode === defaultLanguage)
      ?.name,
    price: dish.price || ''
  }))

  const guests = useMemo<ApiGuest[]>(
    () => ordersData?.guests ?? [],
    [ordersData]
  )

  const totalDishCount = useMemo(
    () => guests.reduce((sum, g) => sum + g.dishes.length, 0),
    [guests]
  )

  const activeGuest = useMemo(
    () => guests.find(g => g.guestNumber === activeGuestNumber),
    [guests, activeGuestNumber]
  )

  const formatPrice = (value: number) =>
    t('COMMON.CURRENCY_FORMAT', { value: (value / 100).toFixed(2) })

  const getTranslation = (translations: TranslationModel[]) =>
    translations.find(t => t.languageCode === defaultLanguage)?.name ??
    translations.find(t => t.languageCode === 'en')?.name ??
    ''

  const mapDishToSummaryItem = (dish: ApiDish) => {
    const name =
      getTranslation(dish.translations) || t('SERVE_ORDER.UNKNOWN_DISH')
    const sizeName = dish.size
      ? getTranslation(dish.size.translations)
      : undefined
    const basePrice = dish.price
    const sizePrice = dish.size?.price ?? dish.price

    const toppings = (dish.toppings ?? []).map(topping => {
      const toppingName = topping.translations
        ? getTranslation(topping.translations)
        : t('SERVE_ORDER.UNKNOWN_TOPPING')
      const toppingPrice = topping.price ?? 0
      return {
        name: toppingName || t('SERVE_ORDER.UNKNOWN_TOPPING'),
        price: formatPrice(toppingPrice),
        rawPrice: toppingPrice
      }
    })

    const toppingsTotal = toppings.reduce((sum, t) => sum + t.rawPrice, 0)
    const totalPrice = sizePrice + toppingsTotal

    return {
      count: 1,
      name,
      dishSize: sizeName || undefined,
      basePrice,
      totalPrice,
      toppings: toppings.map(t => ({ name: t.name, price: t.price })),
      note: dish.extraNote || undefined
    }
  }

  const subtotal = useMemo(
    () =>
      guests.reduce(
        (sum, guest) =>
          sum +
          guest.dishes.reduce(
            (dSum, dish) => dSum + mapDishToSummaryItem(dish).totalPrice,
            0
          ),
        0
      ),
    [guests]
  )
  const tax = subtotal * 0.05
  const total = subtotal + tax

  const handleStatusAction = () => {
    if (tableId) {
      const targetStatus = isPaid ? 'free' : 'served'
      TablesAPI.putTableStatus(tableId, { status: targetStatus }).then(() => {
        socketService.emit(WebSocketEvent.TABLES_UPDATE, { id: tableId })
      })
    }
    navigate('/tables-list')
  }

  const handleBack = () => {
    navigate('/tables-list')
  }

  const renderSummary = () => {
    if (isLoading) {
      return (
        <div className={styles.serveOrderLoading}>
          <i className="las la-spinner icon-large" />
          <p className="body-medium">{t('SERVE_ORDER.LOADING_ORDER')}</p>
        </div>
      )
    }

    if (totalDishCount === 0) {
      return (
        <div className={styles.serveOrderEmpty}>
          <p className="body-medium">{t('SERVE_ORDER.NO_ACTIVE_ORDERS')}</p>
        </div>
      )
    }

    return (
      <>
        <div className={styles.serveOrderGuestsListWrapper}>
          <GuestsList
            guestsNumber={guests.length}
            activeGuest={activeGuestNumber}
            onGuestClick={setActiveGuestNumber}
            showDelete={false}
          />
        </div>
        <div className={styles.serveOrderItemsWrapper}>
          <div className={styles.serveOrderItemsContainer}>
            {activeGuest?.dishes.map((dish, idx) => {
              const item = mapDishToSummaryItem(dish)
              return (
                <SummaryItemComponent
                  key={idx}
                  count={item.count}
                  name={item.name}
                  dishSize={item.dishSize}
                  price={formatPrice(item.totalPrice)}
                  basePrice={
                    item.basePrice !== item.totalPrice
                      ? formatPrice(item.basePrice)
                      : undefined
                  }
                  toppings={item.toppings}
                  note={item.note}
                />
              )
            })}
          </div>
        </div>
      </>
    )
  }

  return (
    <Grid>
      <div className={`start-1 col-7 ${styles.serveOrderMenuWrapper}`}>
        <Tile color="black" customPadding="1rem">
          <div className={styles.serveOrderTilesContainer}>
            <OrderTile
              title={t('ORDER_PAGE.ALL_CATEGORIES')}
              description={t('ORDER_PAGE.ITEMS_COUNT', {
                count: mappedDishesList?.length ?? 0
              })}
              backgroundColor={CATEGORY_PASTEL_COLORS[0]}
              selected={selectedCategoryTile === 0}
              onClick={() => setSelectedCategoryTile(0)}
              index={0}
            />
            {mappedDishCategoriesList?.map((category, index) => (
              <OrderTile
                key={category.id}
                title={category.title || t('ORDER_PAGE.NO_TITLE')}
                description={t('ORDER_PAGE.ITEMS_COUNT', { count: 0 })}
                backgroundColor={
                  CATEGORY_PASTEL_COLORS[
                    (index + 1) % CATEGORY_PASTEL_COLORS.length
                  ]
                }
                selected={selectedCategoryTile === index + 1}
                onClick={() => setSelectedCategoryTile(index + 1)}
                index={index + 1}
              />
            ))}
          </div>
          <Divider
            sx={{
              backgroundColor: primaryColors.white,
              margin: '0.5rem'
            }}
          />
          <div className={styles.serveOrderTilesContainer}>
            {mappedDishesList?.map(dish => (
              <OrderTile
                key={dish.id}
                title={dish.title || t('ORDER_PAGE.NO_TITLE')}
                description={
                  dish.price
                    ? t('COMMON.CURRENCY_FORMAT', {
                        value: (Number(dish.price) / 100).toFixed(2)
                      })
                    : t('ORDER_PAGE.NO_PRICE')
                }
                backgroundColor={whiteShades.shade1}
              />
            ))}
          </div>
        </Tile>
      </div>

      <div className={`start-8 col-5 ${styles.serveOrderSummaryWrapper}`}>
        <Tile
          color="white"
          height="calc(100% - 6.25rem)"
          customPadding="1.5rem"
        >
          <div className={styles.serveOrderSummaryContainer}>
            <p className={`title-bold-medium ${styles.serveOrderHeader}`}>
              {t('SERVE_ORDER.TABLE_ORDER_SUMMARY')}
            </p>

            {renderSummary()}

            <div className={styles.serveOrderButtonsWrapper}>
              <Button
                label={
                  isPaid
                    ? t('SERVE_ORDER.MARK_AS_FREE')
                    : t('SERVE_ORDER.MARK_AS_SERVED')
                }
                variant="contained"
                color="primary"
                onClick={handleStatusAction}
                mobile
                maxWidth
                icon="las la-check-circle icon-large"
                iconLocation="right"
              />
              <Button
                label={t('SERVE_ORDER.BACK_TO_TABLES')}
                variant="contained"
                color="secondary"
                onClick={handleBack}
                mobile
                maxWidth
              />
            </div>
          </div>
        </Tile>
        <div className={styles.serveOrderTotalWrapper}>
          <div className={styles.serveOrderTotalContainer}>
            <p className={`title-medium ${styles.serveOrderTotalLabel}`}>
              {t('SERVE_ORDER.SUBTOTAL')}
            </p>
            <p className={`title-medium ${styles.serveOrderTotalLabel}`}>
              {formatPrice(subtotal)}
            </p>
          </div>
          <div className={styles.serveOrderTotalContainer}>
            <p className={`title-medium ${styles.serveOrderTotalLabel}`}>
              {t('SERVE_ORDER.TAX')}
            </p>
            <p className={`title-medium ${styles.serveOrderTotalLabel}`}>
              {formatPrice(tax)}
            </p>
          </div>
          <div className={styles.serveOrderTotalContainer}>
            <p className={`title-bold-large ${styles.serveOrderTotalLabel}`}>
              {isPaid
                ? t('SERVE_ORDER.PAID_AMOUNT')
                : t('SERVE_ORDER.PAYABLE_AMOUNT')}
            </p>
            <p className={`title-large ${styles.serveOrderTotalLabel}`}>
              {formatPrice(total)}
            </p>
          </div>
        </div>
      </div>
    </Grid>
  )
}

export default ServeOrder
